<template>
  <section>
    <div
      v-if="
        firstTime &&
        !invitedControllingManagerUsers.length &&
        !recognizedControllingManagerUsers.length
      "
    >
      <p class="text-left">
        You’ve selected to apply for LoadPay™. Please provide the administrator
        of the account, known as a Controlling Manager (an individual with
        significant responsibility to control, manage, or direct the legal
        entity applying.)
      </p>
      <div
        v-if="!invitedControllingManagerUsers.length"
        class="mt-6 flex gap-3"
        :class="layoutClass"
      >
        <RoundButton
          v-for="button in buttons"
          :key="button.identifier"
          :checked="isActive === button.identifier"
          :use-radio="button.useRadio"
          @click="handleClick(button.identifier)"
        >
          <div class="flex w-full flex-col items-center justify-center">
            <svg
              v-if="!button.useRadio"
              width="21"
              height="20"
              viewBox="0 0 21 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.5 7V13M13.5 10H7.5M19.5 10C19.5 11.1819 19.2672 12.3522 18.8149 13.4442C18.3626 14.5361 17.6997 15.5282 16.864 16.364C16.0282 17.1997 15.0361 17.8626 13.9442 18.3149C12.8522 18.7672 11.6819 19 10.5 19C9.3181 19 8.14778 18.7672 7.05585 18.3149C5.96392 17.8626 4.97177 17.1997 4.13604 16.364C3.30031 15.5282 2.63738 14.5361 2.18508 13.4442C1.73279 12.3522 1.5 11.1819 1.5 10C1.5 7.61305 2.44821 5.32387 4.13604 3.63604C5.82387 1.94821 8.11305 1 10.5 1C12.8869 1 15.1761 1.94821 16.864 3.63604C18.5518 5.32387 19.5 7.61305 19.5 10Z"
                stroke="#6C7C9D"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <p class="text-neutral-subtle-bold">
              {{ button.label }}
            </p>
          </div>
        </RoundButton>
      </div>
      <div
        v-if="!invitedControllingManagerUsers.length"
        class="mt-6 flex justify-center md:justify-start"
      >
        <RoundButton
          :checked="isActive === 'idk'"
          :use-radio="true"
          @click="handleClick('idk')"
        >
          <p
            :class="[
              isActive === 'idk' ? 'text-dark-gray' : 'text-neutral-subtle',
            ]"
          >
            I don't know this information
          </p>
        </RoundButton>
      </div>
    </div>
    <div>
      <section v-if="invitedControllingManagerUsers.length">
        <p class="mb-2 mt-6 text-left text-neutral-subtle">
          Invited Controlling Manager
        </p>
        <div class="flex flex-col gap-3">
          <div
            v-for="user in invitedControllingManagerUsers"
            :key="user.invited_user_email"
            class="flex items-center justify-between gap-2 rounded-lg bg-off-white px-3 py-5"
          >
            <div class="flex items-center gap-2">
              <CMIcon />
              <p class="text-neutral-subtle">
                {{ user.invited_user_email }}
              </p>
            </div>
          </div>
        </div>
      </section>
      <section v-if="recognizedControllingManagerUsers.length">
        <RecognizedOwners
          :recognized-owners="recognizedControllingManagerUsers"
          :controlling-manager="true"
        />
      </section>
      <section
        v-if="!firstTime && isActive === 'idk'"
        class="opacity-70"
      >
        <RoundButton
          :checked="true"
          :use-radio="true"
          :disabled="true"
          @click="() => {}"
        >
          <p>I don't know this information</p>
        </RoundButton>
      </section>
    </div>

    <ConfirmYourOwnership
      :hide-button="true"
      :trigger-flow="isActive == 'cm'"
      :assign-as-controlling-manager="true"
      @close="handleClose"
      @exec-next-step="handleNext"
    />

    <FormNavigationButtons
      v-if="!isNavigationDisabled"
      :enabled="!isNavigationDisabled"
      :component-identifier="componentIdentifier"
      :buttons-to-display="buttonsToDisplay"
      @next="handleNext"
      @back="handleBack"
      @edit="handleEdit"
    />

    <AddControllingManagerModal
      :visible="openModal"
      :loading="loading"
      @close="closeModal"
      @success="handleInviteControllingManager"
    />

    <ReplaceControllingManagerModal
      :visible="replaceCModal"
      :loading="loading"
      @close="() => (replaceCModal = false)"
      @success="handleReplaceControllingManager"
    />
  </section>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useToast } from 'vue-toast-notification';
import { useStore } from 'vuex';
import RoundButton from '@/components/RoundButton.vue';
import { useFormRouter } from '@/composables/formRouter';
import CMIcon from '@/components/icons/OwnerIcon.vue';
import ConfirmYourOwnership from '@/components/ConfirmYourOwnership.vue';
import FormNavigationButtons from '@/components/FormNavigationButtons.vue';
import AddControllingManagerModal from '@/components/modals/AddControllingManagerModal.vue';
import ReplaceControllingManagerModal from '@/components/modals/ReplaceControllingManagerModal.vue';
import RecognizedOwners from '@/components/steps/Ownership/RecognizedOwners.vue';

const componentIdentifier = 'Payment-5';

const store = useStore();
const toast = useToast();
const { state, commit, dispatch } = store;
const formRouter = useFormRouter(componentIdentifier);

const isActive = ref('');
const loading = ref(false);
const openModal = ref(false);
const replaceCModal = ref(false);
const layoutClass = 'flex-col md:flex-row';

const isNavigationDisabled = computed(() => formRouter.isDisabled.value);
const ownershipData = computed(() => state.ownership.ownershipData);
const onboardingPacketId = computed(() => state.carriers.carrierData?.id);
const isSubmitButtonDisabled = computed(() => state.submitButtonDisabled);
const canManageUsers = computed(() =>
  store.state.users?.currentUser?.scopes?.includes('manage_users')
);
const buttonsToDisplay = computed(() =>
  firstTime.value ? 'continue-save' : 'continue-edit'
);
const firstTime = computed(
  () => state.firstTime?.[formRouter.componentStep.value?.firstTimeIdentifier]
);
const activeStep = computed(() => state.formRoutes?.activeStep?.identifier);

const recognizedControllingManagerUsers = computed(
  () =>
    ownershipData.value?.ownership_users?.filter(
      (user) =>
        user.is_type === 'controlling_manager' &&
        (user.status === 'complete' || user.status === 'started')
    ) || []
);

const invitedControllingManagerUsers = computed(
  () =>
    ownershipData.value?.ownership_users?.filter(
      (user) =>
        user.is_type === 'controlling_manager' && user.status === 'invited'
    ) || []
);

const buttons = computed(() => {
  const opts = [];
  if (!invitedControllingManagerUsers?.value?.length && canManageUsers.value) {
    opts.push({
      identifier: 'cm',
      label: 'I am The Controlling Manager',
      useRadio: false,
    });
  }
  opts.push({
    identifier: 'acm',
    label: 'Add Controlling Manager',
    useRadio: false,
  });
  return opts;
});

const handleReplaceControllingManager = async ({ email }) => {
  if (!email) {
    showToast('Please provide an email for the controlling manager');
    return;
  }

  try {
    loading.value = true;
    const currentUser = store.state.users.currentUser;
    const currentUserOwnership = ownershipData.value?.ownership_users?.find(
      (user) => (user.invited_user_email === currentUser.email) === email
    );

    // Case: the current user is a registered owner
    if (currentUserOwnership) {
      await updateControllingManager(
        currentUserOwnership.ownership_id,
        currentUser.id
      );
      showToast('You are now the Controlling Manager');
      return;
    }

    if (!ownershipData.value) {
      await createOwnershipCompany();
    }

    const existingUser =
      store.state.ownership.ownershipData?.ownership_users.find(
        (user) => user.invited_user_email === email
      );

    if (existingUser) {
      await updateControllingManager(
        existingUser.ownership_id,
        existingUser.invited_user_email
      );

      if (isSubmitButtonDisabled.value) {
        store.commit('toggleDisabledButton', {
          bool: false,
          source: 'PaymentStepFive #5',
        });
      }
      showToast('Controlling Manager has been updated');
    } else {
      handleInviteControllingManager({ email });
    }
  } catch (error) {
    showToast(
      error?.response?.data?.[0] ||
        'Something went wrong with your request. Please try again'
    );
  } finally {
    loading.value = false;
    replaceCModal.value = false;
  }
};

const showToast = (msg) => {
  toast?.open({
    message: msg,
    position: 'top-right',
    duration: 5000,
  });
};

const handleClose = () => {
  isActive.value = '';
  commit('toggleDisabledButton', {
    bool: true,
    source: 'PaymentStepFive #12',
  });
};

const updateControllingManager = async (ownershipId, identifier) => {
  await store.dispatch('ownership/changeControllingManager', {
    ownership_id: ownershipId,
    ...(typeof identifier === 'number'
      ? { user_id: identifier }
      : { email: identifier }),
  });
  await store.dispatch(
    'ownership/getOwnershipByCompanyId',
    store.state.users.currentUser.company.id
  );
};

const createOwnershipCompany = () =>
  dispatch('ownership/createOwnershipCompany', {
    companyId: state.users.currentUser.company.id,
  });

const handleInviteControllingManager = async ({ email }) => {
  if (!email) {
    toast?.open({
      message: 'Please provide an email for the controlling manager',
      position: 'top-right',
      duration: 5000,
    });
    return;
  }
  try {
    loading.value = true;
    if (!ownershipData.value) await createOwnershipCompany();
    const { id: ownershipId } = ownershipData.value || {};
    await dispatch('ownership/createOwnershipUser', {
      ownershipId,
      email: email?.toLowerCase(),
      type: 'controlling_manager',
    });

    await dispatch(
      'ownership/getOwnershipByCompanyId',
      state.users.currentUser.company.id
    );
    if (isSubmitButtonDisabled.value) {
      commit('toggleDisabledButton', {
        bool: false,
        source: 'PaymentStepFive #5',
      });
    }
    toast?.open({
      message: 'Controlling Manager has been invited',
      position: 'top-right',
      duration: 5000,
    });
    handleNext();
  } catch (error) {
    toast?.open({
      message:
        error?.response?.data?.[0] ||
        'Something went wrong with your request. Please try again',
      position: 'top-right',
      duration: 5000,
    });
  } finally {
    loading.value = false;
    closeModal();
  }
};

const handleNext = async () => {
  const selectedOption = isActive.value;
  const storedIdk =
    localStorage.getItem(`payment.step5.idk.${onboardingPacketId.value}`) ===
    'idk';

  if (isActive.value !== 'idk' && storedIdk) {
    localStorage.removeItem(`payment.step5.idk.${onboardingPacketId.value}`);
  }

  if (selectedOption === 'idk' && !storedIdk) {
    await dispatch(
      'ownership/emailForControllingManagerAndOwners',
      state.carriers.carrierData.connection.id
    );
    localStorage.setItem(
      `payment.step5.idk.${onboardingPacketId.value}`,
      'idk'
    );
  }

  await formRouter.moveToNextStep();
};

const handleBack = () => {
  commit('toggleDisabledButton', {
    bool: false,
    source: 'PaymentStepFive #6',
  });
  formRouter.moveToStepByIdentifier('Payment-2');
};
const handleEdit = () => {
  store.commit(
    'addFormDebugMessage',
    componentIdentifier + ": 'handleEdit' called"
  );
  if (isNavigationDisabled.value)
    return console.log(
      'ERROR: ' + componentIdentifier + ': formRouter is disabled, returning'
    );
  formRouter.editActiveStep();
};

const handleClick = (identifier) => {
  isActive.value = identifier;

  store.commit('toggleDisabledButton', {
    bool: !['idk', 'cm'].includes(identifier),
    source: `PaymentStepFive #${['idk', 'cm'].includes(identifier) ? 5 : 4}`,
  });

  if (identifier === 'acm') {
    openModal.value = true;
  }
};

const closeModal = () => {
  openModal.value = false;
  isActive.value = '';
};

watch(activeStep, (newVal) => {
  if (newVal !== componentIdentifier) return;

  store.dispatch(
    'ownership/getOwnershipByCompanyId',
    store.state.users.currentUser.company.id
  );

  const invitedCount = invitedControllingManagerUsers?.value?.length || 0;
  const recognizedCount = recognizedControllingManagerUsers?.value?.length || 0;
  const storedIdk =
    localStorage.getItem(`payment.step5.idk.${onboardingPacketId.value}`) ===
    'idk';

  if (storedIdk) isActive.value = 'idk';

  const hasUsersOrIdk = invitedCount > 0 || recognizedCount > 0 || storedIdk;

  if (hasUsersOrIdk && firstTime.value) {
    store.commit('firstTime/updateStepFirstTime', {
      step: 'Payment',
      value: false,
    });
    return;
  }

  store.commit('firstTime/updateStepFirstTime', {
    step: 'Payment',
    value: !hasUsersOrIdk,
  });
  store.commit('toggleDisabledButton', {
    bool: !hasUsersOrIdk,
    source: hasUsersOrIdk ? 'PaymentStepFive #2' : 'PaymentStepFive #1',
  });
});
</script>
